/**
 * Role logic for Tappd: the single source of "which role is this user?"
 * (Phase 5a). Pure functions with explicit inputs: no hooks, no component or
 * context imports, safe to call from anywhere (guards, Header, mobile nav,
 * pages, tests).
 *
 * Before Phase 5a there were three copies of this logic (Header.jsx inline,
 * navigation/utils/roleUtils.js, routing/guards.jsx) and they disagree for
 * some users. This module keeps every caller's recorded behaviour
 * (src/__tests__/golden/roles.json) by making the disagreement explicit: each
 * predicate takes `ctx.variant`, and each public function defaults to the
 * variant its former copy used.
 *
 *   ROLE_VARIANTS.HEADER   Header.jsx desktop user menu (was inline); also the
 *                          business pages' and AdminLayout's own checks
 *                          (businessPageAccess / adminLayoutAccess)
 *   ROLE_VARIANTS.NAV      roleUtils getUserRole / getDashboardRoles
 *   ROLE_VARIANTS.NAV_ALL  roleUtils getAllUserRoles (looser/narrower checks)
 *   ROLE_VARIANTS.GUARD    routing/guards.jsx has*Access (+ Redeem.jsx)
 *
 * Do NOT "fix" a variant here without an Overseer decision: the differences
 * are listed in src/__tests__/roles.test.js ("documented discrepancies") and
 * changing one changes what some real user sees or can open.
 *
 * D12: a user with only business_ids (no business_id, role or account_type
 * 'advertiser') passes RequireBusiness (GUARD) and is then refused by the
 * business page's own businessPageAccess (HEADER) check: DealForm,
 * BusinessSettings, BusinessDeals and ManageSubscription send them to "/";
 * BusinessDashboard leaves them on its loading spinner (its "Access Required"
 * screen is unreachable). Pinned in src/__tests__/roles.pages.test.jsx.
 */

export const ROLE_VARIANTS = Object.freeze({
  HEADER: 'header',
  NAV: 'nav',
  NAV_ALL: 'navAll',
  GUARD: 'guard',
});

// Role hierarchy (highest to lowest priority)
export const ROLES = {
  ADMIN: 'admin',
  VENUE: 'venue',
  BUSINESS: 'business',
  CONSUMER: 'consumer',
};

// Role color mapping (Tailwind classes)
export const ROLE_COLORS = {
  [ROLES.ADMIN]: {
    gradient: 'from-red-500 to-orange-500',
    bg: 'bg-red-500',
    bgLight: 'bg-red-500/20',
    border: 'border-red-500/30',
    text: 'text-red-400',
    badge: 'bg-red-500/30 text-red-300',
  },
  [ROLES.VENUE]: {
    gradient: 'from-cyan-500 to-teal-500',
    bg: 'bg-cyan-500',
    bgLight: 'bg-cyan-500/20',
    border: 'border-cyan-500/30',
    text: 'text-cyan-400',
    badge: 'bg-cyan-500/20 text-cyan-300',
  },
  [ROLES.BUSINESS]: {
    gradient: 'from-purple-500 to-violet-500',
    bg: 'bg-purple-500',
    bgLight: 'bg-purple-500/20',
    border: 'border-purple-500/30',
    text: 'text-purple-400',
    badge: 'bg-purple-500/20 text-purple-300',
  },
  [ROLES.CONSUMER]: {
    gradient: 'from-orange-500 to-amber-500',
    bg: 'bg-orange-500',
    bgLight: 'bg-orange-500/20',
    border: 'border-orange-500/30',
    text: 'text-orange-400',
    badge: 'bg-orange-500/20 text-orange-300',
  },
};

// Role dashboard routes
export const ROLE_ROUTES = {
  [ROLES.ADMIN]: '/admin/dashboard',
  [ROLES.VENUE]: '/venue/dashboard',
  [ROLES.BUSINESS]: '/business/dashboard',
  [ROLES.CONSUMER]: '/deal-wallet',
};

// Role labels for display
export const ROLE_LABELS = {
  [ROLES.ADMIN]: 'Admin',
  [ROLES.VENUE]: 'Venue',
  [ROLES.BUSINESS]: 'Business',
  [ROLES.CONSUMER]: null, // No badge for consumers
};

// Role CTA labels
export const ROLE_CTA = {
  [ROLES.ADMIN]: {
    title: 'Master Dashboard',
    subtitle: 'Full admin controls',
    shortLabel: 'Admin',
  },
  [ROLES.VENUE]: {
    title: 'Venue Dashboard',
    subtitle: 'Manage coasters & view scans',
    shortLabel: 'Venue',
  },
  [ROLES.BUSINESS]: {
    title: 'Business Dashboard',
    subtitle: 'Manage your deals & analytics',
    shortLabel: 'Business',
  },
  [ROLES.CONSUMER]: {
    title: 'Deal Wallet',
    subtitle: 'View your claimed deals',
    shortLabel: 'Deal Wallet',
  },
};

// ---------------------------------------------------------------------------
// Per-variant predicates. Each returns exactly what its former copy returned
// (raw values included: the Header's business check can return the
// business_id string itself, and callers only use it for truthiness).

const ADMIN_RULES = {
  [ROLE_VARIANTS.HEADER]: (user) =>
    user?.role === 'admin' ||
    user?.role === 'master_admin' ||
    user?.is_admin === true ||
    user?.account_type === 'admin',
  [ROLE_VARIANTS.NAV]: (user) =>
    user?.role === 'admin' ||
    user?.role === 'master_admin' ||
    user?.is_admin === true ||
    user?.is_master_admin === true ||
    user?.admin === true,
  [ROLE_VARIANTS.NAV_ALL]: (user) => user?.role === 'admin' || user?.is_admin,
  [ROLE_VARIANTS.GUARD]: (user) =>
    !!user && (user.role === 'admin' || user.role === 'master_admin' || user.is_admin === true),
};

const BUSINESS_RULES = {
  [ROLE_VARIANTS.HEADER]: (user) =>
    user?.role === 'advertiser' ||
    user?.account_type === 'advertiser' ||
    user?.business_id,
  [ROLE_VARIANTS.NAV]: (user) =>
    user?.business_ids?.length > 0 || user?.business_id || user?.is_business_owner === true || user?.role === 'advertiser',
  [ROLE_VARIANTS.NAV_ALL]: (user) =>
    user?.business_ids?.length > 0 || user?.business_id || user?.is_business_owner || user?.role === 'advertiser',
  [ROLE_VARIANTS.GUARD]: (user) =>
    !!user && (user.account_type === 'advertiser' || user.role === 'advertiser' || !!user.business_id || user.business_ids?.length > 0),
};

const VENUE_RULES = {
  [ROLE_VARIANTS.HEADER]: (user) => user?.role === 'venue_partner',
  [ROLE_VARIANTS.NAV]: (user) =>
    user?.venue_ids?.length > 0 || user?.venue_partner_id || user?.is_venue_partner === true || user?.role === 'venue_partner',
  [ROLE_VARIANTS.NAV_ALL]: (user) =>
    user?.venue_ids?.length > 0 || user?.venue_partner_id || user?.is_venue_partner || user?.role === 'venue_partner',
  [ROLE_VARIANTS.GUARD]: (user) =>
    !!user && (user.role === 'venue_partner' || !!user.venue_partner_id || user.venue_ids?.length > 0 || user.is_venue_partner === true),
};

const rule = (table, ctx, fallback) => {
  const variant = ctx?.variant || fallback;
  const fn = table[variant];
  if (!fn) throw new Error(`Unknown role variant: ${variant}`);
  return fn;
};

/** Admin check. `ctx.variant` picks the caller's rule (default NAV). */
export const isAdminUser = (user, ctx) => rule(ADMIN_RULES, ctx, ROLE_VARIANTS.NAV)(user);
/** Advertiser / business-owner check. `ctx.variant` picks the rule (default NAV). */
export const isBusinessUser = (user, ctx) => rule(BUSINESS_RULES, ctx, ROLE_VARIANTS.NAV)(user);
/** Venue-partner check. `ctx.variant` picks the rule (default NAV). */
export const isVenueUser = (user, ctx) => rule(VENUE_RULES, ctx, ROLE_VARIANTS.NAV)(user);

// ---------------------------------------------------------------------------
// Navigation (former roleUtils.js)

/**
 * Dashboard-worthy roles, highest priority first (admin, venue, business).
 * Excludes consumer. Default variant NAV.
 */
export function getDashboardRoles(user, ctx) {
  if (!user) return [];
  const c = { variant: ROLE_VARIANTS.NAV, ...ctx };
  const roles = [];
  if (isAdminUser(user, c)) roles.push(ROLES.ADMIN);
  if (isVenueUser(user, c)) roles.push(ROLES.VENUE);
  if (isBusinessUser(user, c)) roles.push(ROLES.BUSINESS);
  return roles;
}

/**
 * Primary role: admin > venue > business > consumer; null when signed out.
 * Default variant NAV.
 */
export function getUserRole(user, ctx) {
  if (!user) return null;
  const c = { variant: ROLE_VARIANTS.NAV, ...ctx };
  if (isAdminUser(user, c)) return ROLES.ADMIN;
  if (isVenueUser(user, c)) return ROLES.VENUE;
  if (isBusinessUser(user, c)) return ROLES.BUSINESS;
  return ROLES.CONSUMER;
}

/**
 * Every role the user has, consumer first, then business, venue, admin
 * (note: a different order from getDashboardRoles). Default variant NAV_ALL.
 */
export function getAllUserRoles(user, ctx) {
  if (!user) return [];
  const c = { variant: ROLE_VARIANTS.NAV_ALL, ...ctx };
  const roles = [ROLES.CONSUMER];
  if (isBusinessUser(user, c)) roles.push(ROLES.BUSINESS);
  if (isVenueUser(user, c)) roles.push(ROLES.VENUE);
  if (isAdminUser(user, c)) roles.push(ROLES.ADMIN);
  return roles;
}

/** Dashboard route for the user's primary role (null when signed out). */
export function primaryDashboardPath(user, ctx) {
  const role = getUserRole(user, ctx);
  return role ? ROLE_ROUTES[role] : null;
}

// ---------------------------------------------------------------------------
// Route guards (former routing/guards.jsx). Always booleans.

// A ctx without a variant (or with variant undefined) still means GUARD.
const guardCtx = (ctx) => ({ ...ctx, variant: ctx?.variant || ROLE_VARIANTS.GUARD });

export const hasBusinessAccess = (user, ctx) => isBusinessUser(user, guardCtx(ctx));
export const hasVenueAccess = (user, ctx) => isVenueUser(user, guardCtx(ctx));
export const hasAdminAccess = (user, ctx) => isAdminUser(user, guardCtx(ctx));

// ---------------------------------------------------------------------------
// Page-level checks (former inline copies in the business pages and the admin
// layout). Their expressions are value-identical to the HEADER rules (the
// operand order differs but every operand before the last is a boolean
// comparison), so they reuse that variant. See D12.

/**
 * Business pages' own access check (DealForm, BusinessSettings, BusinessDeals,
 * BusinessDashboard x2, ManageSubscription): HEADER business rule. Raw value
 * (true, the business_id string, or undefined); pages use it for truthiness
 * and as an effect dependency.
 */
export const businessPageAccess = (user) => isBusinessUser(user, { variant: ROLE_VARIANTS.HEADER });

/** AdminLayout's check of the signed-in session user: HEADER admin rule. */
export const adminLayoutAccess = (user) => isAdminUser(user, { variant: ROLE_VARIANTS.HEADER });

// ---------------------------------------------------------------------------
// Header desktop user menu (former Header.jsx inline logic)

/**
 * Which dashboard sections the Header's desktop user menu shows. Raw values:
 * `isAdvertiser` may be the business_id string or undefined (the Header only
 * uses it for `&&`), `isAdmin` / `isVenuePartner` are booleans.
 */
export function headerMenuFlags(user, ctx) {
  const c = { variant: ROLE_VARIANTS.HEADER, ...ctx };
  return {
    isAdmin: isAdminUser(user, c),
    isAdvertiser: isBusinessUser(user, c),
    isVenuePartner: isVenueUser(user, c),
  };
}

/**
 * Get user initials for avatar
 * 
 * @param {Object} user - User object
 * @returns {string} Two-letter initials
 */
export function getUserInitials(user) {
  if (!user) return '??';
  
  // Try first_name + last_name first
  if (user.first_name && user.last_name) {
    return (user.first_name[0] + user.last_name[0]).toUpperCase();
  }
  
  // Try display name
  if (user.display_name) {
    const parts = user.display_name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return user.display_name.slice(0, 2).toUpperCase();
  }
  
  // Try first_name only
  if (user.first_name) {
    return user.first_name.slice(0, 2).toUpperCase();
  }
  
  // Try business/venue name
  if (user.business_name) {
    const parts = user.business_name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return user.business_name.slice(0, 2).toUpperCase();
  }
  
  // Fallback to email
  if (user.email) {
    return user.email.slice(0, 2).toUpperCase();
  }
  
  return '??';
}

/**
 * Get display name for user
 * 
 * @param {Object} user - User object
 * @param {string} role - User's primary role
 * @returns {string} Display name
 */
export function getDisplayName(user, role) {
  if (!user) return 'User';
  
  // For business/venue roles, prefer business/venue name
  if (role === ROLES.BUSINESS && user.business_name) {
    return user.business_name;
  }
  
  if (role === ROLES.VENUE && user.venue_name) {
    return user.venue_name;
  }
  
  // Otherwise use first name, display name or email
  if (user.first_name) {
    return user.first_name;
  }
  
  return user.display_name || user.name || user.email?.split('@')[0] || 'User';
}
